import { Eye, EyeOff } from "lucide-react";
import { useAuthStore } from "@/store/auth/auth.store";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import GoogleBtn from "@/components/auth/google.button";
import { signup } from "@/services/auth.service";
import { useSnackbar } from "notistack";
import { getErrorFromBackend } from "@/utils/errors";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import SignupSchema from "./signup.schema";
import { setAuthToken, setRefreshToken } from "@/utils/axios-instance";
import { useState } from "react";

type SignupFields = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

const SignupForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const methods = useForm<SignupFields>({
    resolver: zodResolver(SignupSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    },
  });
  const { handleSubmit, reset, formState, control } = methods;
  const { isSubmitting } = formState;
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = handleSubmit(async (data) => {
    try {
      const { AccessToken, RefreshToken } = await signup(data);
      setAuthToken(AccessToken);
      setRefreshToken(RefreshToken);
      enqueueSnackbar("Account created successfully", { variant: "success" });
      reset();
      useAuthStore.getState().userLoggedIn();
    } catch (e) {
      enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
    }
  });

  return (
    <Form {...methods}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div className="flex flex-col md:flex-row gap-2 w-full items-start">
          <FormField
            control={control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="text-gray-800">
                  First Name <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    className="bg-white text-black border-gray-400"
                    required
                    placeholder="Enter your First Name"
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-xs text-red-500" />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="text-gray-800">Last Name <span className="text-red-500">*</span></FormLabel>
                <FormControl>
                  <Input
                    className="bg-white text-black border-gray-400"
                    placeholder="Enter your Last Name"
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-xs text-red-500" />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-800">
                Email <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  className="bg-white text-black border-gray-400"
                  type="email"
                  placeholder="Enter your email address"
                  autoComplete="email"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-xs text-red-500" />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-800">
                Password <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    className="bg-white border-gray-400 text-gray-800 pr-10"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    {...field}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </FormControl>
              <FormMessage className="text-xs text-red-500" />
            </FormItem>
          )}
        />
        <Button className="w-full bg-sky-500 hover:bg-sky-700 !mt-8" type="submit" disabled={isSubmitting}>
          Sign Up
        </Button>

        <div className="font-bold my-5 text-center text-gray-800">OR</div>

        <GoogleBtn />
      </form>
    </Form>
  );
};

export default SignupForm;
